import { Inject, Injectable, NgZone } from "@angular/core";
import { Subject } from "rxjs";
import { MatDialog } from "@angular/material/dialog";
import { MmpConfirmPopupComponent } from "src/app/components/mmp-confirm-popup/mmp-confirm-popup.component";
import { AppConfigService } from "src/app/services/app-config.service";
import { MESSAGES } from "src/app/shared/constants/messages.constant";
import { Router } from "@angular/router";
import { ComparisonNormalizer } from "src/app/shared/utils/comparison-normalizer.util";
import { OKTA_AUTH } from "@okta/okta-angular";
import OktaAuth from "@okta/okta-auth-js";

@Injectable({
  providedIn: "root",
})
export class RetryService {
  retryParam: any;
  retryWebsocketUrl: string = "";

  private socket: WebSocket;
  private readonly showConsoleLogs: boolean = false;
  retrySubject = new Subject<any>();
  retryResult$ = this.retrySubject.asObservable();

  constructor(
    private readonly dialog: MatDialog,
    private readonly envService: AppConfigService,
    private readonly zone: NgZone,
    private readonly router: Router,
    @Inject(OKTA_AUTH) private readonly oktaAuth: OktaAuth,
  ) {
    this.showConsoleLogs = this.envService.showConsoleLogs;
    this.retryWebsocketUrl = this.envService.mmpWebsocketRetryUrl;
  }

  checkRetryRequired(currentResult, stage) {
    const entireSession = currentResult.map((session) => session.sessionKey);

    // Filter sessions that are not completed or have empty output
    const incompleteSessions = currentResult
      .filter((session) => {
        const output = session.output;
        // Check if output is a string and trim it, or handle non-string cases
        return (
          ComparisonNormalizer.string(session.status) !== "completed" ||
          (typeof output === "string" && output.trim() === "") ||
          (typeof output === "object" && Object.keys(output).length === 0) // Check if output is an empty object
        );
      })
      .map((session) => session.sessionKey);

    if (incompleteSessions.length > 0) {
      this.retryParam = {
        stage: stage,
        session_ids: entireSession,
        pendig_session: incompleteSessions,
      };
      // Show confirmation dialog
      this.showRetryConfirmationDialog();
      return;
    }

    if (this.showConsoleLogs) {
      console.log(MESSAGES.RETRY.RETRY_NOT_REQUIRED);
    }
  }

  private showRetryConfirmationDialog(): void {
    const dialogRef = this.dialog.open(MmpConfirmPopupComponent, {
      width: "500px",
      disableClose: true,
      data: {
        title: "Something Went Wrong",
        message: MESSAGES.RETRY.CONFIRM_MESSAGE,
        cancelText: "Go to Dashboard",
        confirmText: "Try Again",
        hideCancelButton: false,
        errorPopup: true,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (ComparisonNormalizer.string(result) === "yes") {
        this.startRetry();
      } else {
        // Navigate to dashboard
        this.router.navigate(["/evaluator-dashboard"]);
      }
    });
  }

  private startRetry() {
    // Retry logic
    if (this.showConsoleLogs) {
      console.log("retry started");
    }

    this.socket = new WebSocket(this.retryWebsocketUrl);

    this.socket.onopen = async () => {
      const accessToken = this.oktaAuth.getAccessToken();

      this.socket.send(
        JSON.stringify({
          type: "auth",
          token: accessToken,
        }),
      );

      this.socket.send(JSON.stringify(this.retryParam));

      const intervalId = setInterval(() => {
        if (this.socket.readyState === WebSocket.OPEN) {
          this.socket.send(
            JSON.stringify({
              type: MESSAGES.WEBSOCKET.HANDSHAKE_TYPE,
              text: MESSAGES.WEBSOCKET.UPLOAD_HANDSHAKE_MESSAGE,
            }),
          ); //Handshake senting to backend(server)
          if (this.showConsoleLogs) {
            console.log(MESSAGES.WEBSOCKET.FIXED_MESSAGE_SENT);
          }
        } else {
          if (this.showConsoleLogs) {
            console.log(MESSAGES.WEBSOCKET.HANDSHAKE_STOPED);
          }

          clearInterval(intervalId);
        }
      }, 10000);
    };

    this.socket.onmessage = (event) => {
      // Ensure Angular change detection runs
      this.zone.run(() => this.handleMessage(event));
    };

    this.socket.onerror = (error) => {
      if (this.showConsoleLogs) {
        console.error("WebSocket error:", error);
      }
    };

    this.socket.onclose = (event) => {
      if (event.wasClean) {
        if (this.showConsoleLogs) {
          console.log(
            MESSAGES.WEBSOCKET.CLOSED_CLEAN,
            event.code,
            event.reason,
          );
        }

        if (
          ComparisonNormalizer.string(event.reason) === "keepalive ping timeout"
        ) {
          //likely 1011
          if (this.showConsoleLogs) {
            console.log(
              MESSAGES.WEBSOCKET.CLOSED_CLEAN,
              event.code,
              event.reason,
            );
          }
          this.zone.run(() => {
            this.showRetryConfirmationDialog();
          });
        }
      } else {
        if (this.showConsoleLogs) {
          console.warn(
            MESSAGES.WEBSOCKET.CLOSED_UNEXPECTED,
            event.code,
            event.reason,
          );
        }
        this.zone.run(() => {
          this.showRetryConfirmationDialog();
        });
      }
    };
  }

  private handleMessage(event: MessageEvent) {
    const data = JSON.parse(event.data);

    if (ComparisonNormalizer.string(data.status) === "completed") {
      this.disconnect();
    } else {
      const stage = this.retryParam.stage;
      this.retrySubject.next({
        type: stage,
        payload: data,
      });
    }
  }

  disconnect() {
    if (this.showConsoleLogs) {
      console.log(MESSAGES.RETRY.RETRY_COMPLETED);
    }
    this.socket?.close();
  }

  public showAgentRetryConfirmationDialog(): Promise<boolean> {
    return new Promise((resolve) => {
      const dialogRef = this.dialog.open(MmpConfirmPopupComponent, {
        width: "500px",
        disableClose: true,
        data: {
          title: "Something Went Wrong",
          message: MESSAGES.RETRY.CONFIRM_MESSAGE,
          cancelText: "Go to Dashboard",
          confirmText: "Try Again",
          hideCancelButton: false,
          errorPopup: true,
        },
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (ComparisonNormalizer.string(result) === "yes") {
          resolve(true);
        } else {
          if (this.showConsoleLogs) {
            console.log("Retry canceled.");
          }
          resolve(false);
          // Navigate to dashboard
          this.router.navigate(["/evaluator-dashboard"]);
        }
      });
    });
  }
}
